import type { Artist } from '../../interfaces/maestra';
import { assinaturaValeComoPro, perfilEstaAtivo } from '../../nucleo/perfilAtivo';
import { useAppSelector } from '../../store/store';
import type { Ir } from '../porta';

// O CAMINHO PARA O PRO, NUM LUGAR SÓ (pedido do Lucas, 04/10/2026: "o fluxo tá diferente em cada
// lugar que eu clico"). Cada botão que vende o PRO perguntava só "o perfil está ativo?" e mandava
// todo mundo para o mesmo lugar. Mas o dono de um perfil não ativo está numa de quatro situações, e
// cada uma tem o seu caminho:
//
//   · GRÁTIS: assinar (os planos, ou o desbloqueio do perfil, conforme a tela);
//   · PAGAMENTO PENDENTE: o PIX já foi gerado e não venceu. Abrir outro checkout gerava um QR novo
//     por cima do que ainda vale: o caminho é RETOMAR (/pagamento, que reabre a mesma cobrança);
//   · PRO COM O PERFIL FORA DA COTA: a pessoa já assina; assinar de novo era cobrar duas vezes. O
//     caminho é incluir o perfil na assinatura (/planos/ajustar);
//   · EM ATRASO, fora da carência: regularizar a assinatura (/planos, o painel com a cobrança).
//
// O convidado nunca recebe venda (ele não é quem paga): a situação dele é `membro`.

export type SituacaoDoPro = 'ativo' | 'membro' | 'gratis' | 'pendente' | 'foraDaCota' | 'atrasado';

export const situacaoDoPro = (e: { status: string; isPro: boolean; perfilAtivo: boolean; membro: boolean }): SituacaoDoPro => {
  if (e.perfilAtivo) return 'ativo';
  if (e.membro) return 'membro';
  if (e.status === 'pending') return 'pendente';
  if (e.isPro) return 'foraDaCota';
  if (e.status === 'overdue') return 'atrasado';
  return 'gratis';
};

export type SituacaoEspecial = 'pendente' | 'foraDaCota' | 'atrasado';

/** O que dizer e para onde ir nas três situações que não são a do plano grátis. */
export const CAMINHO_DO_PRO: Record<SituacaoEspecial, { rotulo: string; destino: string; titulo: string; texto: string }> = {
  pendente: {
    rotulo: 'Retomar o pagamento',
    destino: '/pagamento',
    titulo: 'O pagamento do Maestra PRO está pendente',
    texto: 'O PIX já foi gerado. Pague o mesmo código, sem começar do zero, e tudo abre assim que o pagamento cair.',
  },
  foraDaCota: {
    rotulo: 'Incluir este perfil na assinatura',
    destino: '/planos/ajustar',
    titulo: 'Este perfil está fora da sua assinatura',
    texto: 'Você já assina o Maestra PRO, mas a assinatura cobre menos perfis do que você tem. Inclua este para usar o planejamento e os módulos.',
  },
  atrasado: {
    rotulo: 'Regularizar a assinatura',
    destino: '/planos',
    titulo: 'A sua assinatura está com um pagamento em atraso',
    texto: 'Regularize o pagamento para voltar a usar o planejamento e os módulos.',
  },
};

/** O pendente no cartão não tem PIX: é a operadora analisando (QA do pagamento, 04/10/2026). */
const PENDENTE_NO_CARTAO = {
  rotulo: 'Ver o pagamento',
  destino: '/pagamento',
  titulo: 'O pagamento do Maestra PRO está em análise',
  texto: 'A operadora do cartão está processando o pagamento. Tudo abre assim que ele for aprovado.',
};

const especialDa = (situacao: SituacaoDoPro, billingType?: string | null) => {
  if (situacao === 'pendente' && billingType === 'CREDIT_CARD') return PENDENTE_NO_CARTAO;
  return situacao === 'pendente' || situacao === 'foraDaCota' || situacao === 'atrasado' ? CAMINHO_DO_PRO[situacao] : null;
};

/** O caminho para o PRO deste perfil, para quem olha agora. */
export const useCaminhoDoPro = (artist: Artist | null | undefined, ir: Ir) => {
  // A assinatura que a casca já carrega. Sem ela (ainda chegando), a conta conta como grátis: o
  // pior caso é o botão de assinar, que a /planos e o checkout corrigem com o estado do servidor.
  const assinatura = useAppSelector((s) => (s as { subscription?: { status?: string; gracePeriodEndsAt?: string | null; billingType?: string | null } }).subscription);
  const status = assinatura?.status ?? 'none';
  const isPro = assinaturaValeComoPro(status, assinatura?.gracePeriodEndsAt ?? null);
  const situacao = situacaoDoPro({ status, isPro, perfilAtivo: perfilEstaAtivo(artist, isPro), membro: artist?.role === 'member' });
  const especial = especialDa(situacao, assinatura?.billingType);
  return {
    situacao,
    /** As três situações que não são o grátis (null no grátis: cada tela usa o seu convite). */
    especial,
    /** O rótulo do botão: o da situação, ou o da tela no plano grátis. */
    rotulo: (doGratis: string) => especial?.rotulo ?? doGratis,
    /**
     * Vai ao caminho da situação; no plano grátis, ao destino da tela (os planos, ou o desbloqueio).
     * Fora da cota, o ajuste leva qual perfil incluir: pago, é ESTE que fica ativo (C5).
     */
    ir: (doGratis = '/planos') => ir(
      !especial ? doGratis
        : situacao === 'foraDaCota' && artist?.id ? `${especial.destino}?incluir=${artist.id}`
          : especial.destino,
    ),
  };
};

export type CaminhoDoPro = ReturnType<typeof useCaminhoDoPro>;
